import React, { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  Popover,
  Typography,
} from "@mui/material";
import { FaFilter } from "react-icons/fa";
import { AssetType, SourceType } from "@nendaTypes/cms/CmsAssetType";
import {
  EMPTY_FILTERS,
  SOURCE_TYPE_OPTIONS,
  VOD_TYPE_OPTIONS,
  VODFilters,
} from "./vodListParams";

interface VODFilterProps {
  filters: VODFilters;
  onChange: (filters: VODFilters) => void;
}

const VODFilter: React.FC<VODFilterProps> = ({ filters, onChange }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [draft, setDraft] = useState<VODFilters>(filters);

  const activeFilterCount = filters.type.length + filters.sourceType.length;

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    setDraft(filters);
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleApply = () => {
    onChange(draft);
    handleClose();
  };

  const handleClear = () => {
    setDraft(EMPTY_FILTERS);
    onChange(EMPTY_FILTERS);
    handleClose();
  };

  const handleTypeToggle = (value: AssetType) => {
    const next = draft.type.includes(value)
      ? draft.type.filter((t) => t !== value)
      : [...draft.type, value];
    setDraft({ ...draft, type: next });
  };

  const handleSourceTypeToggle = (value: SourceType) => {
    const next = draft.sourceType.includes(value)
      ? draft.sourceType.filter((s) => s !== value)
      : [...draft.sourceType, value];
    setDraft({ ...draft, sourceType: next });
  };

  return (
    <>
      <Badge
        badgeContent={activeFilterCount > 0 ? activeFilterCount : undefined}
        color="primary"
      >
        <IconButton
          size="small"
          onClick={handleOpen}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "0.7rem",
            height: "38px",
            width: "38px",
          }}
          title="Filter"
        >
          <FaFilter size={14} />
        </IconButton>
      </Badge>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          "& .MuiPopover-paper": {
            borderRadius: "0.6rem",
            background: "white",
            boxShadow: "20px 20px 50px rgba(0, 0, 0, 0.1)",
            border: "none",
          },
        }}
      >
        <Box sx={{ p: 2, minWidth: 200, bgcolor: "background.paper" }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Type
          </Typography>
          <FormGroup>
            {VOD_TYPE_OPTIONS.map(({ value, label }) => (
              <FormControlLabel
                key={value}
                control={
                  <Checkbox
                    size="small"
                    checked={draft.type.includes(value)}
                    onChange={() => handleTypeToggle(value)}
                  />
                }
                label={label}
              />
            ))}
          </FormGroup>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Source type
          </Typography>
          <FormGroup>
            {SOURCE_TYPE_OPTIONS.map(({ value, label }) => (
              <FormControlLabel
                key={value}
                control={
                  <Checkbox
                    size="small"
                    checked={draft.sourceType.includes(value)}
                    onChange={() => handleSourceTypeToggle(value)}
                  />
                }
                label={label}
              />
            ))}
          </FormGroup>
          <Divider sx={{ my: 1.5 }} />
          <Box
            sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}
          >
            <Button
              size="small"
              variant="text"
              color="inherit"
              onClick={handleClear}
              disabled={
                draft.type.length === 0 && draft.sourceType.length === 0
              }
            >
              Clear
            </Button>
            <Button size="small" variant="contained" onClick={handleApply}>
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default VODFilter;
